import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Tabs, Tab, Paper, Typography, Button, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton,
  TextField, Checkbox, MenuItem,
} from '@mui/material';
import { Edit as EditIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { SupplierService } from '../../services/supplier.service';
import { SupplierItemService } from '../../services/supplierItem.service';
import { ItemService } from '../../services/item.service';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { SupplierItem, SupplierItemCreateDTO } from '../../interfaces/procurement/supplierItem.interface';
import { Item } from '../../interfaces/item.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { formatMoney } from '../../utils/format';
import ScrollableTable from '../common/ScrollableTable';
import { ConfirmDialog } from '../common/ConfirmDialog';
import SearchableSelect from '../common/SearchableSelect';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { ActiveStatusChip, DeactivateDialog } from '../common/MasterDataStatus';
import { SupplierPaymentProfile } from './paymentrun/SupplierPaymentProfile'; // W3-P
import SupplierPortalCard from './supplierportal/SupplierPortalCard'; // F8

export const SupplierDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  // The statement lives on /procurement/reports, which is gated by SUPPLIER_BILL_VIEW.
  const canManage = usePermission(PERMISSIONS.SUPPLIER_MANAGE);
  const canViewBills = usePermission(PERMISSIONS.SUPPLIER_BILL_VIEW);
  const canPortal = usePermission(PERMISSIONS.SUPPLIER_PORTAL_MANAGE); // F8
  const [tab, setTab] = useState(0);
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [catalogue, setCatalogue] = useState<SupplierItem[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [newRow, setNewRow] = useState<{ itemId: string; unitPrice: number; preferred: boolean }>({
    itemId: '', unitPrice: 0, preferred: false,
  });
  const [rowPendingDelete, setRowPendingDelete] = useState<string | null>(null);
  const [deletingRow, setDeletingRow] = useState(false);

  useEffect(() => { loadAll(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    try {
      setLoading(true);
      const [s, c, itemsRes] = await Promise.all([
        SupplierService.getById(id!),
        SupplierItemService.listBySupplier(id!),
        // Read-only oversight roles may lack item access; the catalogue then shows "—" for names.
        ItemService.getItemsByInstitution(0, 200).catch(() => ({ content: [] as Item[] })),
      ]);
      setSupplier(s);
      setCatalogue(c);
      setItems(itemsRes.content);
    } catch (e) {
      toast.showApiError(e, 'Failed to load supplier detail');
    } finally {
      setLoading(false);
    }
  };

  const addCatalogueRow = async () => {
    if (!newRow.itemId || newRow.unitPrice <= 0) {
      toast.showApiError(null, 'Pick an item and enter a positive price');
      return;
    }
    try {
      const dto: SupplierItemCreateDTO = {
        supplierId: id!,
        itemId: newRow.itemId,
        unitPrice: newRow.unitPrice,
        preferred: newRow.preferred,
        institutionId: AuthService.getSelectedInstitution()!!,
      };
      await SupplierItemService.create(dto);
      setNewRow({ itemId: '', unitPrice: 0, preferred: false });
      const c = await SupplierItemService.listBySupplier(id!);
      setCatalogue(c);
    } catch (e) {
      toast.showApiError(e, 'Failed to add catalogue row');
    }
  };

  const deleteCatalogueRow = async () => {
    if (!rowPendingDelete) return;
    setDeletingRow(true);
    try {
      await SupplierItemService.delete(rowPendingDelete);
      setCatalogue(c => c.filter(r => r.id !== rowPendingDelete));
      setRowPendingDelete(null);
    } catch (e) {
      toast.showApiError(e, 'Failed to delete catalogue row');
    } finally {
      setDeletingRow(false);
    }
  };

  const activateSupplier = async () => {
    try {
      await SupplierService.activate(supplier!.id!);
      toast.showSuccess('Supplier activated');
      loadAll();
    } catch (e) {
      toast.showApiError(e, 'Failed to activate supplier');
    }
  };

  if (loading || !supplier) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5">{supplier.name}</Typography>
          <ActiveStatusChip active={supplier.active} reason={supplier.deactivationReason} />
        </Box>
        {canManage && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {supplier.active === false ? (
              <Button data-help="procurement.suppliers.activate" variant="outlined" onClick={activateSupplier}>Activate</Button>
            ) : (
              <Button data-help="procurement.suppliers.deactivate" variant="outlined" color="warning" onClick={() => setDeactivateOpen(true)}>Deactivate</Button>
            )}
            <Button
              data-help="procurement.suppliers.edit"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/procurement/suppliers/${id}/edit`)}
            >
              Edit
            </Button>
          </Box>
        )}
      </Box>
      <DeactivateDialog
        open={deactivateOpen}
        noun="supplier"
        name={supplier.name}
        onDeactivate={async (reason) => {
          try {
            return await SupplierService.deactivate(supplier.id!, reason);
          } catch (e) {
            toast.showApiError(e, 'Failed to deactivate supplier');
            throw e;
          }
        }}
        // refresh just the supplier in place: loadAll() would swap the page for a spinner and unmount this dialog
        onDeactivated={async () => {
          try { setSupplier(await SupplierService.getById(supplier.id!)); } catch { /* refreshed on the next load */ }
        }}
        onClose={() => setDeactivateOpen(false)}
      />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Overview" />
        <Tab label="Catalogue" />
        <Tab label="Purchase History" />
        <Tab label="Payment details" /> {/* W3-P */}
        {canPortal && <Tab label="Portal" value={4} />} {/* F8 */}
      </Tabs>

      {tab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 1, alignItems: 'center' }}>
            <Typography color="text.secondary">Contact</Typography>
            <Typography>{supplier.contactPerson ?? '—'}</Typography>
            <Typography color="text.secondary">Email</Typography>
            <Typography>{supplier.email ?? '—'}</Typography>
            <Typography color="text.secondary">Phone</Typography>
            <Typography>{supplier.phone ?? '—'}</Typography>
            <Typography color="text.secondary">Address</Typography>
            <Typography>{supplier.address ?? '—'}</Typography>
            <Typography color="text.secondary">Tax Reg No</Typography>
            <Typography>{supplier.taxRegistrationNo ?? '—'}</Typography>
          </Box>
        </Paper>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          <ScrollableTable>
              <Table size="small" data-help-section="procurement.supplierDetail.catalogue">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="procurement.supplierDetail.col.item">Item</TableCell>
                    <TableCell data-help="procurement.supplierDetail.col.price">Unit Price</TableCell>
                    <TableCell data-help="procurement.supplierDetail.col.preferred">Preferred</TableCell>
                    {canManage && <TableCell data-help="procurement.supplierDetail.col.actions" align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {catalogue.map(c => (
                    <TableRow key={c.id}>
                      <TableCell>{items.find(i => i.id === c.itemId)?.itemName ?? '—'}</TableCell>
                      <TableCell>{formatMoney(c.unitPrice)}</TableCell>
                      <TableCell>{c.preferred ? '✓' : ''}</TableCell>
                      {canManage && (
                        <TableCell align="right">
                          <IconButton data-help="procurement.supplierDetail.action.remove" size="small" onClick={() => setRowPendingDelete(c.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {canManage && (<TableRow>
                    <TableCell data-help="procurement.supplierDetail.field.item">
                      <SearchableSelect<Item>
                        options={items}
                        value={newRow.itemId}
                        onChange={id => setNewRow({ ...newRow, itemId: id })}
                        getOptionLabel={i => i.itemName}
                        placeholder="Select item"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        data-help="procurement.supplierDetail.field.price"
                        type="number"
                        size="small"
                        value={newRow.unitPrice}
                        onChange={e => setNewRow({ ...newRow, unitPrice: Number(e.target.value) })}
                      />
                    </TableCell>
                    <TableCell>
                      <Checkbox
                        data-help="procurement.supplierDetail.field.preferred"
                        checked={newRow.preferred}
                        onChange={e => setNewRow({ ...newRow, preferred: e.target.checked })}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton data-help="procurement.supplierDetail.action.add" size="small" color="primary" onClick={addCatalogueRow}>
                        <AddIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>)}
                </TableBody>
              </Table>
          </ScrollableTable>
        </Paper>
      )}

      {tab === 2 && (
        <Paper sx={{ p: 2 }}>
          {/* Purchase history: v1 = simple "see Purchase Orders list filtered by this supplier" link */}
          <Button
            data-help="procurement.supplierDetail.action.orders"
            variant="text"
            onClick={() => navigate(`/procurement/purchase-orders?supplierId=${id}`)}
          >
            View Purchase Orders for this Supplier →
          </Button>
          {canViewBills && (
            <Button
              data-help="procurement.supplierDetail.action.statement"
              variant="text"
              onClick={() => navigate(`/procurement/reports?supplierId=${id}`)}
            >
              Payables statement →
            </Button>
          )}
        </Paper>
      )}

      {/* W3-P */}
      {tab === 3 && id && <SupplierPaymentProfile supplierId={id} canManage={canManage} />}
      {/* F8 */}
      {tab === 4 && id && canPortal && <SupplierPortalCard supplierId={id} />}

      <ConfirmDialog
        open={rowPendingDelete !== null}
        title="Delete this catalogue row?"
        message="This cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        onCancel={() => setRowPendingDelete(null)}
        onConfirm={deleteCatalogueRow}
        submitting={deletingRow}
      />
    </Box>
  );
};
